// VIDEO-EDITING-MVP — caption preset registry.
//
// Phase 3a ships ONLY the Trust Bar preset. Phase 3b expands to 8.
//
// **Lockstep contract**: the field set on CaptionPreset is mirrored by
// `SERVER_PRESETS` in `core/src/routes/video/captions/export/route.ts`.
// When you change a field name or add a preset id here, update the
// SERVER_PRESETS table there in the same change — the ASS generator
// reads the same fields the FE overlay reads. See plan §3.6.
//
// `accentColor`, `backgroundColor`, `textStroke`, `uppercase`, and
// `letterSpacing` are the fields that materially change the burned-in
// caption render; keep their names stable.
import type { CSSProperties } from "react";
import { z } from "zod";

import { CAPTION_PRESET_IDS } from "@wiser/remotion-layers";

import {
  OVERLAY_FONT_FAMILIES,
  type OverlayFontFamily,
} from "./overlays";

export { CAPTION_PRESET_IDS };

export type CaptionAlignment = "left" | "center" | "right";
export type CaptionAnimation =
  | "fade"
  | "pop"
  | "wordReveal"
  | "slideUp"
  | "static";

export interface CaptionPreset {
  id: string;
  name: string;
  positionY: number;
  alignment: CaptionAlignment;
  fontWeight: number;
  fontSize: number;
  color: string;
  accentColor?: string;
  backgroundColor?: string;
  textStroke?: string;
  textShadow?: string;
  uppercase?: boolean;
  letterSpacing?: number;
  maxLines: number;
  animation: CaptionAnimation;
  inset: number;
}

// Phase 3b — full 8-preset library per plan §3.6.
// The corresponding SERVER_PRESETS table in
// `core/src/routes/video/captions/export/route.ts` must stay 1:1 with
// these entries on the fields the ASS generator consumes:
//   id, positionY, alignment, fontWeight, fontSize, color, accentColor?,
//   backgroundColor?, textStroke?, uppercase?, letterSpacing?.
// Add a preset there in the same change you add one here.
export const PRESETS: CaptionPreset[] = [
  {
    id: "trust-bar",
    name: "Trust Bar",
    positionY: 0.86,
    alignment: "center",
    fontWeight: 900,
    fontSize: 0.055,
    color: "#FFFFFF",
    backgroundColor: "rgba(0,0,0,0.7)",
    maxLines: 2,
    animation: "fade",
    inset: 8,
  },
  {
    id: "hero-headline",
    name: "Hero Headline",
    positionY: 0.22,
    alignment: "center",
    fontWeight: 900,
    fontSize: 0.08,
    color: "#FFFFFF",
    textStroke: "4px #000",
    uppercase: true,
    letterSpacing: 0.02,
    maxLines: 2,
    animation: "pop",
    inset: 8,
  },
  {
    id: "word-by-word-accent",
    name: "Word-by-Word Accent",
    positionY: 0.5,
    alignment: "center",
    fontWeight: 800,
    fontSize: 0.07,
    color: "#FFFFFF",
    accentColor: "#FFD400",
    textStroke: "3px #000",
    maxLines: 3,
    animation: "wordReveal",
    inset: 6,
  },
  {
    id: "two-line-conversational",
    name: "Two-Line Conversational",
    positionY: 0.78,
    alignment: "left",
    fontWeight: 700,
    fontSize: 0.045,
    color: "#FFFFFF",
    textStroke: "2px rgba(0,0,0,0.85)",
    maxLines: 2,
    animation: "fade",
    inset: 6,
  },
  {
    id: "quote-card",
    name: "Quote Card",
    positionY: 0.5,
    alignment: "center",
    fontWeight: 700,
    fontSize: 0.06,
    color: "#F5F0E6",
    backgroundColor: "rgba(0,0,0,0.55)",
    maxLines: 3,
    animation: "static",
    inset: 15,
  },
  {
    id: "sticker-bar",
    name: "Sticker Bar",
    positionY: 0.9,
    alignment: "center",
    fontWeight: 900,
    fontSize: 0.05,
    color: "#FFFFFF",
    backgroundColor: "rgba(217,56,31,0.95)",
    maxLines: 1,
    animation: "slideUp",
    inset: 12,
  },
  {
    id: "hook-body-stack",
    name: "Hook + Body Stack",
    positionY: 0.82,
    alignment: "center",
    fontWeight: 700,
    fontSize: 0.045,
    color: "#FFFFFF",
    textStroke: "2px rgba(0,0,0,0.9)",
    maxLines: 2,
    animation: "fade",
    inset: 8,
  },
  {
    id: "caption-lite",
    name: "Caption Lite",
    positionY: 0.9,
    alignment: "center",
    fontWeight: 600,
    fontSize: 0.038,
    color: "rgba(255,255,255,0.92)",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
    maxLines: 2,
    animation: "static",
    inset: 10,
  },
];

export const DEFAULT_PRESET_ID = "trust-bar";

export function getPreset(id: string | null | undefined): CaptionPreset {
  if (!id) return PRESETS[0];
  return PRESETS.find((p) => p.id === id) ?? PRESETS[0];
}

export interface CustomCaptionStyle {
  positionY?: number;
  fontSize?: number;
  color?: string;
  fontFamily?: OverlayFontFamily;
  fontWeight?: number;
  // Active-word highlight color. Overrides preset.accentColor on
  // word-by-word-accent and on any non-accent preset that wants per-word
  // emphasis. The literal "__rainbow__" sentinel is rendered as a 6-color
  // per-word cycle (see captionPaletteCatalog.RAINBOW_CYCLE) by both the
  // Remotion renderer and the ASS burn-in path.
  accentColor?: string;
}

export const MIN_CUSTOM_FONT_SIZE = 0.02;
export const MAX_CUSTOM_FONT_SIZE = 0.2;
const STYLE_LENGTH_BASE_HEIGHT_PX = 720;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function formatCqh(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return `${rounded}cqh`;
}

function pxToVideoHeightUnit(px: number): string {
  return formatCqh((px / STYLE_LENGTH_BASE_HEIGHT_PX) * 100);
}

function scaleCssPxLengths(value: string): string {
  return value.replace(/(-?\d*\.?\d+)px/g, (_match, raw: string) =>
    pxToVideoHeightUnit(Number(raw)),
  );
}

export function clampCustomFontSize(n: number): number {
  if (!Number.isFinite(n)) return MIN_CUSTOM_FONT_SIZE;
  return Math.max(MIN_CUSTOM_FONT_SIZE, Math.min(MAX_CUSTOM_FONT_SIZE, n));
}

export function normalizeCustomFontSize(n: number): number {
  const scaled = n > 1 ? n / 1000 : n;
  return clampCustomFontSize(scaled);
}

export function normalizeCustomCaptionStyle(
  style: CustomCaptionStyle | null | undefined,
): CustomCaptionStyle | null {
  if (!style) return style ?? null;
  return {
    ...style,
    ...(style.positionY != null ? { positionY: clamp01(style.positionY) } : {}),
    ...(style.fontSize != null
      ? { fontSize: normalizeCustomFontSize(style.fontSize) }
      : {}),
  };
}

export const CustomCaptionStyleSchema = z.object({
  positionY: z.number().optional(),
  fontSize: z
    .preprocess(
      (value) =>
        typeof value === "number" ? normalizeCustomFontSize(value) : value,
      z.number(),
    )
    .optional(),
  color: z.string().optional(),
  fontFamily: z.enum(OVERLAY_FONT_FAMILIES).optional(),
  fontWeight: z.number().optional(),
  accentColor: z.string().optional(),
});

export { OVERLAY_FONT_FAMILIES, type OverlayFontFamily };

export const CAPTION_FONT_WEIGHTS = [400, 500, 600, 700, 800, 900] as const;

function effectivePositionY(
  preset: CaptionPreset,
  custom?: CustomCaptionStyle | null,
): number {
  return custom?.positionY != null ? clamp01(custom.positionY) : preset.positionY;
}

function effectiveFontSize(
  preset: CaptionPreset,
  custom?: CustomCaptionStyle | null,
): number {
  return custom?.fontSize != null
    ? normalizeCustomFontSize(custom.fontSize)
    : preset.fontSize;
}

export function presetToTextStyle(
  preset: CaptionPreset,
  custom?: CustomCaptionStyle | null,
): CSSProperties {
  const fontSize = effectiveFontSize(preset, custom);
  const style: CSSProperties = {
    color: custom?.color ?? preset.color,
    fontWeight: custom?.fontWeight ?? preset.fontWeight,
    fontSize: `${fontSize * 100}cqh`,
    textAlign: preset.alignment,
    lineHeight: 1.2,
    textTransform: preset.uppercase ? "uppercase" : "none",
  };
  if (custom?.fontFamily) {
    style.fontFamily = `'${custom.fontFamily}', system-ui, sans-serif`;
  }
  if (preset.letterSpacing != null) {
    style.letterSpacing = `${preset.letterSpacing}em`;
  }
  if (preset.textStroke) {
    const [width, ...rest] = preset.textStroke.split(" ");
    const color = rest.join(" ");
    style.WebkitTextStroke = `${scaleCssPxLengths(width)} ${color}`;
    // Paint the stroke BEHIND the fill so it reads as an outer outline. libass
    // (the burn-in) draws its outline as an outer border, but the browser
    // default paints a CSS text-stroke OVER the fill — eating ~half the stroke
    // width into every glyph, which makes the caption look thin/hollow and
    // diverge from the burned clip. `paint-order: stroke` restores the bold,
    // outer-outlined look 1:1 with the burn.
    style.paintOrder = "stroke";
  }
  if (preset.textShadow) {
    style.textShadow = scaleCssPxLengths(preset.textShadow);
  }
  return style;
}

export function presetToBoxStyle(
  preset: CaptionPreset,
  custom?: CustomCaptionStyle | null,
): CSSProperties {
  const positionY = effectivePositionY(preset, custom);
  const style: CSSProperties = {
    position: "absolute",
    left: `${preset.inset}%`,
    right: `${preset.inset}%`,
    top: `${positionY * 100}%`,
    transform: "translateY(-50%)",
    display: "flex",
    justifyContent:
      preset.alignment === "left"
        ? "flex-start"
        : preset.alignment === "right"
          ? "flex-end"
          : "center",
    pointerEvents: "none",
  };
  return style;
}

export function presetToPillStyle(preset: CaptionPreset): CSSProperties {
  const style: CSSProperties = {
    padding: preset.backgroundColor ? "0.4em 0.8em" : "0",
    borderRadius: preset.backgroundColor ? "0.5em" : "0",
    background: preset.backgroundColor ?? "transparent",
    maxWidth: "100%",
    display: "inline-block",
  };
  return style;
}
